'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, BookOpen, Globe, Heart, Star, Users, Lightbulb, Shield } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const pillars = [
  {
    icon: Globe,
    title: 'Explore Nigeria',
    description: 'Discover all 37 states — their history, culture, food, festivals, languages and landmarks.',
  },
  {
    icon: BookOpen,
    title: 'Stories',
    description: 'Read original Nigerian stories that celebrate heritage, values and imagination.',
  },
  {
    icon: Star,
    title: 'Languages',
    description: 'Learn words and phrases in Yoruba, Igbo and Hausa through guided lessons and vocabulary games.',
  },
  {
    icon: Lightbulb,
    title: 'Life School',
    description: 'Build everyday skills — from emotional intelligence and decision-making to money basics and personal safety.',
  },
  {
    icon: Users,
    title: 'Educational Games',
    description: 'Reinforce learning through six interactive games designed to make knowledge stick.',
  },
  {
    icon: Shield,
    title: 'Safe for Children',
    description: 'A fully child-safe platform. No adverts, no social features, no external links. Parents stay in control.',
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] justify-center sm:justify-start sm:min-w-0 sm:px-2"
            aria-label="Back to home"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <div className="flex items-center gap-2 ml-1">
            <AppLogo size={26} />
            <span className="font-semibold text-sm text-foreground">Naija Explorer</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-primary/5 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-5">
            <Heart size={26} className="text-primary" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
            About Naija Explorer
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            An educational platform designed to help children discover Nigeria — its states, cultures, stories, languages, life skills and educational games.
          </p>
        </div>
      </section>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-12">

        {/* Our mission */}
        <section>
          <h2 className="text-xl font-bold text-foreground mb-4">Our Mission</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed space-y-4">
            <p>
              Naija Explorer was created with a simple belief: every Nigerian child deserves to know and be proud of where they come from.
            </p>
            <p>
              Nigeria is one of the most culturally rich countries in the world — home to hundreds of ethnic groups, languages, traditions and stories. Yet many children grow up with limited access to this heritage in an engaging, age-appropriate format.
            </p>
            <p>
              Naija Explorer brings Nigeria to life for children aged 6–14 through interactive learning, original stories, language lessons, life skills education and games — all in one safe, parent-managed environment.
            </p>
          </div>
        </section>

        {/* What children learn */}
        <section>
          <h2 className="text-xl font-bold text-foreground mb-6">What Children Learn</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pillars?.map((pillar) => {
              const PillarIcon = pillar?.icon;
              return (
                <div
                  key={pillar?.title}
                  className="flex gap-4 p-4 rounded-xl bg-card border border-border"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <PillarIcon size={17} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">{pillar?.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{pillar?.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* For parents */}
        <section>
          <h2 className="text-xl font-bold text-foreground mb-4">For Parents</h2>
          <div className="bg-card border border-border rounded-xl p-5 sm:p-6 space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Naija Explorer is designed with parents in mind. Every child account is created and managed by a parent or guardian. Parents can monitor their child's progress, review completed lessons and track achievements from a dedicated Parent Dashboard.
            </p>
            <p>
              The platform contains no advertising, no social features and no links to external websites. All content has been written and reviewed to be age-appropriate, accurate and respectful of Nigeria's diverse cultures.
            </p>
            <p>
              We take children's privacy seriously. Please read our{' '}
              <Link href="/privacy" className="text-primary hover:underline font-medium">
                Privacy Policy
              </Link>{' '}
              to understand how we handle your family's data.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-xl font-bold text-foreground mb-4">Get in Touch</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Have a question, a suggestion, or need support? We would love to hear from you.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold transition-all active:scale-95 hover:opacity-90"
          >
            Contact Support
          </Link>
        </section>

        {/* Legal links */}
        <section className="pt-4 border-t border-border">
          <div className="flex flex-wrap gap-4 text-sm">
            <Link href="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link href="/terms" className="text-muted-foreground hover:text-primary transition-colors">
              Terms of Use
            </Link>
            <Link href="/contact" className="text-muted-foreground hover:text-primary transition-colors">
              Support &amp; Contact
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
